/**
 * HeaderBrandBlock.tsx — MER Algo Footprints
 *
 * Phase 2: Global Trading Header — Row 1 (Brand Bar, 78px).
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ [logo] MER       Algo Footprints           [Raw][Engine][AI]    │
 *   │                  AI INTRADAY DECISION ENGINE · v{APP_VERSION}    │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * Pure presentational — no data dependencies. The legend chips are static
 * UI tokens documenting the three-tier evidence model used elsewhere in
 * the application. They are NOT live signal indicators.
 *
 * Version note: pulls from the canonical APP_VERSION in
 * src/shared/config/version.ts. Keep this in sync with bump-version.sh's
 * touch-points; do NOT hardcode versions here.
 */

import React from 'react';
import { HeaderStatusPill } from './HeaderStatusPill';
import { APP_VERSION } from '../../shared/config/version';

export interface HeaderBrandBlockProps {
  appVersion?: string;     // shown in subtitle; defaults to canonical APP_VERSION
  showLegend?: boolean;    // collapses on tablet
}

export function HeaderBrandBlock({
  appVersion = APP_VERSION,
  showLegend = true,
}: HeaderBrandBlockProps) {
  return (
    <div
      className="mer-brand-bar"
      style={{
        background: 'var(--mer-bg-top)',
        height: 78,
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        borderBottom: '1px solid var(--mer-border)',
      }}
    >
      {/* Left — logo + MER text */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span
          aria-hidden="true"
          style={{
            width: 38,
            height: 38,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            background: 'linear-gradient(135deg, rgba(166,108,255,.18), rgba(24,245,162,.18))',
            border: '1px solid var(--mer-border)',
          }}
        >
          <svg viewBox="0 0 20 24" width="22" height="26" role="img" aria-label="MER">
            <path
              d="M1 1 L19 1 L19 15 Q19 22 10 24 Q1 22 1 15 Z"
              fill="rgba(13,31,61,0.9)"
              stroke="var(--mer-yellow)"
              strokeWidth="1"
            />
            <polyline
              points="3,18 7,13 10,9 13,11 17,14"
              fill="none"
              stroke="var(--mer-yellow)"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span
          style={{
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: 2,
            color: 'var(--mer-yellow)',
            fontFamily: "'Courier New', monospace",
          }}
        >
          MER
        </span>
      </div>

      {/* Center — title + subtitle */}
      <div className="mer-brand-title" style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--mer-text)', letterSpacing: 0.4 }}>
          Algo Footprints
        </span>
        <span
          style={{
            fontSize: 10,
            letterSpacing: 1.6,
            color: 'var(--mer-label)',
            textTransform: 'uppercase',
            fontWeight: 500,
          }}
        >
          AI Intraday Decision Engine · v{appVersion}
        </span>
      </div>

      {/* Right — legend pills */}
      {showLegend && (
        <div className="mer-brand-legend" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <HeaderStatusPill tone="muted"  size="sm" label="Raw Data" title="Direct market data and ticks" />
          <HeaderStatusPill tone="green"  size="sm" label="Engine"   title="Deterministic engine outputs" />
          <HeaderStatusPill tone="purple" size="sm" label="AI Thesis" title="AI-augmented analysis" />
        </div>
      )}
    </div>
  );
}
